import { useRef, useState, useEffect } from "react";
import { TriggerArrow } from "./trigger-arrow/trigger-arrow";
import { TriggerPlus } from "./trigger-plus/trigger-plus";
import { useResizeDetector } from 'react-resize-detector';
import './collapsible.scss';
import {ICollapsibleArrowProps} from "./collapsible.types";
import TriggerArrow2 from "./trigger-arrow2/trigger-arrow2";

export interface ICollapsibleProps {
    isExpanded?: boolean;
    iconVariant?: COLLAPSIBLE_TRIGGER_ICON_VARIANT;
    iconPosition?: ICON_POSITION;
    colorVariant?: COLLAPSIBLE_COLOR_VARIANT;
    title: React.ReactNode;
    children: React.ReactNode;
    onToggle?: (isExpanded: boolean) => void;
}

export enum COLLAPSIBLE_TRIGGER_ICON_VARIANT {
    PLUS = 'plus_minus',
    ARROW = 'arrow',
    ARROW2 = 'arrow2',
}

/* Notice: if another color scheme is needed - implement it via this variant */
export enum COLLAPSIBLE_COLOR_VARIANT {
    DEFAULT = 'white',
    ACCENT = 'accent',
    PRIMARY2 = 'primary2',
}

export enum ICON_POSITION {
    LEFT = 'left',
    RIGHT = 'right',
}

const Collapsible: React.FC<ICollapsibleProps> = ({
    isExpanded = false,
    iconVariant = COLLAPSIBLE_TRIGGER_ICON_VARIANT.ARROW,
    iconPosition = ICON_POSITION.RIGHT,
    colorVariant = COLLAPSIBLE_COLOR_VARIANT.DEFAULT,
    title = '',
    children = '',
    onToggle,
}) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [_isExpanded, setIsExpanded] = useState(isExpanded!);
    const [heightPixels, setHeightPixels] = useState(isExpanded ? contentRef.current?.scrollHeight : 0);

    useResizeDetector<HTMLDivElement>({
        targetRef: contentRef,
        onResize: () => {
            if (contentRef.current && _isExpanded) {
                setHeightPixels(contentRef.current?.scrollHeight);
            }
        },
    });

    useEffect(() => {
        setIsExpanded(isExpanded as boolean);
        setHeightPixels(isExpanded ? (contentRef.current?.scrollHeight ?? 0) : 0);
    }, [isExpanded]);

    const toggleExpand = () => {
        if (!children) return;
        setIsExpanded(!_isExpanded);
        setHeightPixels(!_isExpanded ? (contentRef.current?.scrollHeight ?? 0) : 0);

        if (onToggle) {
            onToggle(!_isExpanded);
        }
    }

    const getColorClass = () => {
        switch (colorVariant) {
            case COLLAPSIBLE_COLOR_VARIANT.ACCENT:
                return 'collapsible-color-accent';
            case COLLAPSIBLE_COLOR_VARIANT.PRIMARY2:
                return 'collapsible-color-primary2';
            default:
                return 'collapsible-color-default';
        }
    }
    
    const colorClass = getColorClass();

    const getIconComponent = (): React.FC<ICollapsibleArrowProps> => {
        switch (iconVariant) {
            case COLLAPSIBLE_TRIGGER_ICON_VARIANT.PLUS:
                return TriggerPlus;
            case COLLAPSIBLE_TRIGGER_ICON_VARIANT.ARROW:
                return TriggerArrow;
            case COLLAPSIBLE_TRIGGER_ICON_VARIANT.ARROW2:
                return TriggerArrow2;
            default:
                throw new Error(`Unsupported Collapsible icon variant "${iconVariant}"`);
        }
    }

    const ArrowComponent = getIconComponent();
    const iconPositionClass = iconPosition === ICON_POSITION.LEFT ? 'icon-left' : 'icon-right';
        
    return (
        <div className={`collapsible ${colorClass} ${children ? 'collapsible-with-children' : ''} ${_isExpanded ? 'is-expanded' : ''}`}>
            <div className={`collapsible-header ${iconPositionClass}`} onClick={toggleExpand}>
                <div className={`collapsible-label`}>
                    {title}
                </div>
                {/* When there is no content - don't display trigger element and behave just as regular component */}
                {children &&
                    <div className={`collapsible-trigger`}>
                        <ArrowComponent isExpanded={_isExpanded} />
                    </div>}
            </div>
            <div className={`collapsible-content-container`} style={{ height: `${heightPixels}px` }}>
                <div className={`collapsible-content`} ref={contentRef}>
                    { children }
                </div>
            </div>
        </div>
    );
};

export default Collapsible;